import { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle2, XCircle, Loader2, Download } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ImportLog {
  id: string;
  filename: string;
  total_rows: number;
  processed_rows: number;
  created_count: number;
  updated_count: number;
  failed_count: number;
  status: string;
  created_at: string;
  completed_at: string | null;
}

interface BulkBacklinkImportProps {
  userId: string;
  onImportComplete: () => void;
}

export const BulkBacklinkImport = ({ userId, onImportComplete }: BulkBacklinkImportProps) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [currentImport, setCurrentImport] = useState<ImportLog | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const processCSV = async (file: File) => {
    setUploading(true);
    
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or invalid');
        setUploading(false);
        return;
      }

      // Parse CSV
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      // Create import log
      const { data: logData, error: logError } = await supabase
        .from('backlink_import_logs')
        .insert({
          agent_id: userId,
          filename: file.name,
          total_rows: rows.length,
          status: 'processing'
        })
        .select()
        .single();

      if (logError) throw logError;
      setCurrentImport(logData);

      // Process via edge function
      const { data, error } = await supabase.functions.invoke('process-backlink-csv', {
        body: {
          rows,
          import_log_id: logData.id,
          agent_id: userId
        }
      });

      if (error) throw error;

      // Refresh import log
      const { data: updatedLog } = await supabase
        .from('backlink_import_logs')
        .select()
        .eq('id', logData.id)
        .single();

      if (updatedLog) {
        setCurrentImport(updatedLog);
      }

      toast.success(`Import complete! ${data.created} created, ${data.updated} updated, ${data.failed} failed`);
      onImportComplete();
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to process CSV file');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        processCSV(file);
      } else {
        toast.error('Please upload a CSV file');
      }
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processCSV(e.target.files[0]);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Automated Backlink Import
              </CardTitle>
              <CardDescription>
                Upload a CSV file to automatically process all backlinks
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('/files/backlink_import_template.csv', '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-border'
            } ${uploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer hover:border-primary/50'}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileInput}
              className="hidden"
            />
            
            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
                <div>
                  <p className="text-lg font-medium">Processing CSV...</p>
                  <p className="text-sm text-muted-foreground">
                    Validating, creating, and updating backlinks automatically
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground" />
                <div>
                  <p className="text-lg font-medium">Drop your CSV file here</p>
                  <p className="text-sm text-muted-foreground">
                    or click to browse files
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Expected columns: website_name, website_url, category, domain_authority, 
                  contact_name, contact_email, backlink_url, backlink_type, keywords, 
                  anchor_text, property_id, notes
                </p>
              </div>
            )}
          </div>

          {currentImport && (
            <Alert className="mt-4">
              {currentImport.status === 'completed' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : currentImport.status === 'failed' ? (
                <XCircle className="h-4 w-4 text-red-600" />
              ) : (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">{currentImport.filename}</p>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total:</span>{' '}
                      <span className="font-medium">{currentImport.total_rows}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Created:</span>{' '}
                      <span className="font-medium text-green-600">{currentImport.created_count}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Updated:</span>{' '}
                      <span className="font-medium text-blue-600">{currentImport.updated_count}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Failed:</span>{' '}
                      <span className="font-medium text-red-600">{currentImport.failed_count}</span>
                    </div>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};